import { googleLogout } from '@react-oauth/google';
import { eNums } from '../constants';
import { usePermissions } from '../contexts/permissions_context';
import * as ApiService from './apis'
import cookieStorage from './cookie_storage';

export const dateFormatter = (dateString) => {
  if (dateString) {
    const options = {
      day: "2-digit",
      month: "short",
      year: "numeric"
    };
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', options);
  }
  else {
    return '-';
  }
};

export const dateTimeFormatter = (dateString) => {
  if (dateString) {
    const options = {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true, // change to false for 24-hour format
    };
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', options);
  } else {
    return '-';
  }
};

export const useRolePermissions = () => {
  const { permissions } = usePermissions();
  return permissions || null;  // No need for JSON.parse() since permissions are already an object
};

export const capitalizeFirstLetter = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1)?.toLowerCase();
}


// Function to handle file upload and return preview URL and response
export const uploadSingleBlobFile = (e) => {
  try {
    const file = e?.target?.files?.[0];

    if (!file) return { previewUrl: '', response: null };

    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", "file");

    const reader = new FileReader();

    return new Promise((resolve, reject) => {
      reader.onloadend = async () => {
        const previewUrl = reader.result;

        try {
          const res = await ApiService.singleFileUpload(formData);
          resolve({ previewUrl, res: res });
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = (error) => reject(error);

      reader.readAsDataURL(file);
    });
  } catch (error) {
    return { previewUrl: '', res: null };
  }
}

export const getBlobFile = async (fileName) => {
  try {
    const response = await ApiService.fileDownload({
      fileName: fileName,
    });

    const base64String = response?.base64Data;
    const mimeType = response?.mimeType || 'image/jpeg';

    if (base64String) {
      return `data:${mimeType};base64,${base64String}`;
    } else {
      return ''
    }
  } catch (error) {
    return '';
  }
}

export const previewFileInNewTab = async( fileUrl, fileName ) => {
  // If the backend sends generic type, fix it using filename

  const response = await ApiService.fileDownload({
      fileName: fileUrl,
    });

    const base64Data = response?.base64Data;
    let mimeType = response?.mimeType || 'image/jpeg';

  if (mimeType === "application/octet-stream" && fileName) {
    const ext = fileName.split(".").pop().toLowerCase();
    const extMap = {
      pdf: "application/pdf",
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      gif: "image/gif",
      txt: "text/plain",
      html: "text/html",
    };
    mimeType = extMap[ext] || "application/octet-stream";
  }

  // Create proper data URL
  const dataUrl = `data:${mimeType};base64,${base64Data}`;

  // Open in new tab (this works now because mimeType is correct)
  const newTab = window.open();
  if (newTab) {
    newTab.document.write(`
      <html>
        <head><title>${fileName || "Preview"}</title></head>
        <body style="margin:0;padding:0;overflow:hidden;">
          <iframe src="${dataUrl}" style="border:none;width:100%;height:100vh;"></iframe>
        </body>
      </html>
    `);
  } else {
    alert("Please allow popups for this site");
  }
}

export const exportBlobFile = (base64Data, fileName) => {
  // Create an anchor element
  const link = document.createElement('a');

  // Set the download attribute with the desired file name
  link.download = fileName;

  // Set the href of the link to the base64 image data
  link.href = base64Data;

  // Append the link to the body
  document.body.appendChild(link);

  // Trigger a click event on the link
  link.click();

  // Remove the link from the document
  document.body.removeChild(link);
}

export const handleLogout = () => {
  const userType = cookieStorage.getItem('userType');
  cookieStorage.clear();   // clears cookies, localStorage and sessionStorage
  googleLogout();

  const redirectPath = `/${userType !== eNums?.CLIENT ? "agent/" : ""}signin`;
  window.location.assign(redirectPath);
};

export const timeAgo = (dateString) => {
  if (!dateString) return '-';

  const date = new Date(dateString);
  const now = new Date(); // current time (local timezone)

  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 0) return 'just now'; // future date

  let interval = seconds / 31536000; // years
  if (interval >= 1) {
    const years = Math.floor(interval);
    return years === 1 ? '1 yr ago' : `${years} yr ago`;
  }

  interval = seconds / 2592000; // months
  if (interval >= 1) {
    const months = Math.floor(interval);
    return months === 1 ? '1 mon ago' : `${months} mon ago`;
  }

  interval = seconds / 86400; // days
  if (interval >= 1) {
    const days = Math.floor(interval);
    return days === 1 ? '1d ago' : `${days}d ago`;
  }

  interval = seconds / 3600; // hours
  if (interval >= 1) {
    const hours = Math.floor(interval);
    return hours === 1 ? '1h ago' : `${hours}h ago`;
  }

  interval = seconds / 60; // minutes
  if (interval >= 1) {
    const minutes = Math.floor(interval);
    return minutes === 1 ? '1m ago' : `${minutes}m ago`;
  }

  // less than 1 minute
  if (seconds < 10) return 'just now';
  return `${Math.floor(seconds)}s ago`;
};